import { create } from 'zustand'
import type { Theme, User } from '../types'
import type { StudySection } from '../config/study-sections'

export interface ToastMessage { id: string; title: string; message?: string; tone: 'success' | 'error' | 'info' }

interface AppState {
  user: User | null
  hydrated: boolean
  sidebarOpen: boolean
  notificationOpen: boolean
  theme: Theme
  online: boolean
  toasts: ToastMessage[]
  /** Sections the user has an active subscription for */
  subscribedSections: StudySection[]
  /** The section currently active in the app session */
  activeSection: StudySection | null
  setUser: (user: User | null) => void
  setHydrated: (value: boolean) => void
  setSidebarOpen: (value: boolean) => void
  setNotificationOpen: (value: boolean) => void
  setTheme: (theme: Theme) => void
  setOnline: (value: boolean) => void
  addToast: (toast: Omit<ToastMessage, 'id'>) => void
  removeToast: (id: string) => void
  setActiveSection: (section: StudySection) => void
  setSubscribedSections: (sections: StudySection[]) => void
}

const storedTheme = (localStorage.getItem('imed.theme') as Theme | null) ??
                    (localStorage.getItem('MM_theme') as Theme | null) ??
                    (localStorage.getItem('mingming_theme') as Theme | null) ??
                    'light'
const defaultSubscriptions: StudySection[] = ['usmle', 'amc', 'mccqe', 'ukmla-plab', 'smle-gulf', 'postgraduate', 'undergraduate']

const defaultOfflineUser: User = {
  id: 'user-default-saleem',
  email: 'saleem@smc.edu.pk',
  username: 'saleemullah',
  name: 'Saleem Ullah',
  role: 'admin',
  exam: 'USMLE Step 1',
  country: '',
  streak: 5,
  xp: 1250,
  level: 3,
  subscription: 'ultimate',
  createdAt: new Date().toISOString(),
}

// Restore persisted active section and subscriptions from localStorage
const storedActiveSection = (localStorage.getItem('imed.active-section') as StudySection | null)
const storedSubscriptions: StudySection[] = (() => {
  try {
    const raw = localStorage.getItem('imed.subscriptions')
    return raw ? (JSON.parse(raw) as StudySection[]) : defaultSubscriptions
  } catch {
    return defaultSubscriptions
  }
})()

export const useAppStore = create<AppState>((set, get) => ({
  user: defaultOfflineUser,
  hydrated: false,
  sidebarOpen: false,
  notificationOpen: false,
  theme: storedTheme,
  online: navigator.onLine,
  toasts: [],
  subscribedSections: storedSubscriptions,
  activeSection: storedActiveSection,

  setUser: (user) => set({ user }),
  setHydrated: (hydrated) => set({ hydrated }),
  setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
  setNotificationOpen: (notificationOpen) => set({ notificationOpen }),
  setTheme: (theme) => {
    localStorage.setItem('imed.theme', theme)
    localStorage.setItem('MM_theme', theme)
    localStorage.setItem('mingming_theme', theme)
    document.documentElement.setAttribute('data-theme', theme)
    document.documentElement.dataset.theme = theme
    set({ theme })
  },
  setOnline: (online) => set({ online }),

  addToast: (toast) => {
    const id = crypto.randomUUID()
    set({ toasts: [...get().toasts, { ...toast, id }] })
    window.setTimeout(() => get().removeToast(id), 4200)
  },
  removeToast: (id) => set({ toasts: get().toasts.filter((t) => t.id !== id) }),

  setActiveSection: (section) => {
    localStorage.setItem('imed.active-section', section)
    set({ activeSection: section })
  },

  setSubscribedSections: (sections) => {
    localStorage.setItem('imed.subscriptions', JSON.stringify(sections))
    set({ subscribedSections: sections })
  },
}))
